/*
 * The launch queue (U24). Lists the brand's runs with live status, a "new run"
 * affordance (operator → the 4-step Launch wizard at /launch), and inline
 * go/pause/resume/cancel controls enabled per the allowed transition for each
 * run's (status, control). A control mutation optimistically invalidates the run
 * list (the shared `useRunControl` refetch), so the table reflects the new state
 * immediately.
 */

import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Box, Group, Pagination, Stack, Text } from "@mantine/core";
import {
  Button,
  EmptyState,
  PageHeader,
  Table,
  Mono,
  type Column,
} from "../components/ui";
import { StatusPill } from "../components/StatusPill";
import { useAuth } from "../auth/AuthContext";
import { useRuns } from "../api/queries";
import { errorMessage } from "../api/errors";
import type { RunSummary } from "../api/client";
import { Alert } from "../components/ui";
import { ControlBar } from "../components/pipeline";
import { formatDateTime } from "../lib/format";

function shortId(id: string): string {
  return id.length > 12 ? `${id.slice(0, 8)}…` : id;
}

const PAGE_SIZE = 20;

export function QueuePage() {
  const navigate = useNavigate();
  const { can } = useAuth();
  // U10: numbered pagination, 20/page. A fresh page is a fresh query key, so a
  // page transition shows the loading state (not stale rows from the prior page).
  const [page, setPage] = useState(1);
  const runsQuery = useRuns(PAGE_SIZE, (page - 1) * PAGE_SIZE);

  const runs = runsQuery.data?.items ?? [];
  const total = runsQuery.data?.total ?? 0;
  const pageCount = Math.ceil(total / PAGE_SIZE);

  // Recover from an out-of-range page — e.g. a brand switch (or deletions) shrinks
  // the list while the operator is on a later page, which would otherwise strand
  // them on a blank table with no pager. Clamp back to the last valid page. (review fix)
  useEffect(() => {
    if (total > 0 && page > pageCount) setPage(pageCount);
  }, [total, pageCount, page]);

  const columns: Array<Column<RunSummary>> = [
    {
      key: "run",
      header: "Run",
      render: (run) => <Mono>{shortId(run.id)}</Mono>,
      width: "120px",
    },
    {
      key: "product",
      header: "Product",
      render: (run) => run.product_name,
    },
    {
      key: "status",
      header: "Status",
      render: (run) => (
        <Group gap="xs" wrap="wrap" display="inline-flex">
          <StatusPill kind="run" value={run.status} />
          {run.control && run.control !== "none" && (
            <StatusPill
              tone={run.control === "cancelling" ? "danger" : "warning"}
              label={run.control === "cancelling" ? "Cancelling…" : "Pausing…"}
              hideDot
            />
          )}
        </Group>
      ),
    },
    {
      key: "stage",
      header: "Stage",
      render: (run) =>
        run.current_stage ? (
          <Mono>{run.current_stage}</Mono>
        ) : (
          <Text component="span" c="dimmed">
            —
          </Text>
        ),
    },
    {
      key: "ads",
      header: "Ads",
      align: "right",
      render: (run) => (
        <Text component="span" c="dimmed">
          {run.n_ads}
        </Text>
      ),
      width: "60px",
    },
    {
      key: "updated",
      header: "Updated",
      render: (run) => (
        <Text component="span" c="dimmed" fz="xs">
          {formatDateTime(run.updated_at)}
        </Text>
      ),
    },
    {
      key: "controls",
      header: "",
      align: "right",
      render: (run) => (
        <ControlBar
          runId={run.id}
          status={run.status}
          control={run.control}
          size="sm"
        />
      ),
    },
  ];

  return (
    <>
      <PageHeader
        title="Queue"
        description="Launch runs across your products and watch them progress live."
        actions={
          can("operate") ? (
            <Button
              variant="primary"
              onClick={() => navigate("/launch")}
              disabled={runsQuery.isLoading}
            >
              New run
            </Button>
          ) : undefined
        }
      />

      {runsQuery.isError && (
        <Box mb="md">
          <Alert tone="error">
            Couldn't load runs: {errorMessage(runsQuery.error)}
          </Alert>
        </Box>
      )}

      {runsQuery.isLoading ? (
        <EmptyState icon="◷" title="Loading runs…" />
      ) : total === 0 ? (
        <EmptyState
          icon="▦"
          title="No runs yet"
          description="Launch a product to see its run here with live per-stage status."
          action={
            can("operate") ? (
              <Button variant="primary" onClick={() => navigate("/launch")}>
                New run
              </Button>
            ) : undefined
          }
        />
      ) : (
        <Stack gap="md">
          <Table
            columns={columns}
            rows={runs}
            rowKey={(run) => run.id}
            onRowClick={(run) => navigate(`/runs/${run.id}`)}
          />
          {/* Hide the pager for a single page (total ≤ 20). */}
          {pageCount > 1 && (
            <Group justify="center">
              <Pagination total={pageCount} value={page} onChange={setPage} />
            </Group>
          )}
        </Stack>
      )}
    </>
  );
}
